import { useEffect, useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { ROTULOS_ROTA } from "@/lib/modulos"
import { useTituloInstitucional } from "@/modules/cadastro/hooks/useTituloInstitucional"

import { CommandPalette } from "@/app/layouts/CommandPalette"
import { NotificationsMenu, ThemeToggle } from "@/app/layouts/header-actions"
import { useNavGroups } from "@/app/layouts/nav"
import { useBreadcrumbOverrides } from "@/app/layouts/useBreadcrumb"

function Breadcrumbs({ pathname }: { pathname: string }) {
  const overrides = useBreadcrumbOverrides()
  const parts = pathname.split("/").filter(Boolean)
  const crumbs: { label: string; to?: string }[] =
    pathname === "/"
      ? [{ label: "Início" }]
      : parts.map((part, i) => {
          const to = "/" + parts.slice(0, i + 1).join("/")
          return {
            label: overrides[to] ?? ROTULOS_ROTA[part] ?? part,
            to,
          }
        })

  return (
    <nav aria-label="Trilha de navegação" className="min-w-0 text-sm">
      <ol className="flex min-w-0 items-center gap-1.5 overflow-hidden text-muted-foreground">
        {crumbs.map((crumb, i) => (
          <li key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && (
              <span aria-hidden className="shrink-0">
                /
              </span>
            )}
            {crumb.to && i < crumbs.length - 1 ? (
              <NavLink to={crumb.to} className="truncate hover:text-foreground">
                {crumb.label}
              </NavLink>
            ) : (
              <span className="truncate font-medium text-foreground">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function AppHeader() {
  const [commandOpen, setCommandOpen] = useState(false)
  const location = useLocation()
  const groups = useNavGroups()
  useTituloInstitucional()

  useEffect(() => {
    function aoPressionarTecla(evento: KeyboardEvent) {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === "k") {
        evento.preventDefault()
        setCommandOpen(true)
      }
    }

    window.addEventListener("keydown", aoPressionarTecla)
    return () => window.removeEventListener("keydown", aoPressionarTecla)
  }, [])

  return (
    <>
      <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b bg-background px-4 sm:px-6 lg:px-8 2xl:px-10">
        <SidebarTrigger />
        <Breadcrumbs pathname={location.pathname} />
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="hidden text-muted-foreground md:flex"
            onClick={() => setCommandOpen(true)}
          >
            <Search />
            <span>Buscar…</span>
            <kbd className="ml-4 rounded border bg-muted px-1 text-[10px]">Ctrl K</kbd>
          </Button>
          <ThemeToggle />
          <NotificationsMenu />
        </div>
      </header>
      <CommandPalette groups={groups} open={commandOpen} onOpenChange={setCommandOpen} />
    </>
  )
}
